import { useCallback, useMemo, useState } from 'react';
import { useAllowedActions, useNavigationContext, useSafeMutation } from 'hooks';
import { examDatesForLocationsStore } from 'store';
import { ActionEnum, ExamDatesForLocationDto, ExamDatesForLocationItem } from '../common/types';
import {
  DateAvailability,
  SessionDateAvailabilityDto,
  SessionDateAvailabilityRequest,
} from 'api/planning';
import { formatApiDate, parseApiDate } from 'utils';
import { Panel } from '../common/Panel';
import {
  compareDateOnly,
  compareMonthAndYearOnly,
  getFirstDayOfCurrentMonth,
  getNewState,
} from '../common/utilities';

export const useExamDatesForLocationsDistrictContext = () => {
  const { canEditExamDatesForLocations: isEditable } = useAllowedActions();
  const [tableData, setTableData] = useState<ExamDatesForLocationDto>();
  const firstDayOfCurrentMonth = getFirstDayOfCurrentMonth();
  const [selectedDate, setSelectedDate] = useState<Date>(firstDayOfCurrentMonth);
  const [isLoadingButtons, setIsLoadingButtons] = useState(false);

  const isModified = useMemo(
    () => tableData?.dates.some((dateItem) => dateItem.action !== ActionEnum.None) ?? false,
    [tableData]
  );

  const { locationId, locationName } = useNavigationContext();

  const { data, isFetching: isDataLoading } =
    examDatesForLocationsStore.service.useGetSessionDateAvailabilitiesDistrictContextQuery({
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      locationId,
    });

  const [saveSessionDateAvailabilities] =
    examDatesForLocationsStore.service.useSaveSessionDateAvailabilitiesMutation();

  const { execute: saveChanges } = useSafeMutation(saveSessionDateAvailabilities, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: () => setIsLoadingButtons(false),
    onError: () => setIsLoadingButtons(false),
  });

  const generateExamDatesForSelectedDate = useCallback(
    (currentDate: Date, data: SessionDateAvailabilityDto | undefined) => {
      if (!data) {
        return [];
      }

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const dates: ExamDatesForLocationItem[] = Array.from(
        { length: daysInMonth },
        (_, index_y) => {
          const date = new Date(year, month, index_y + 1);

          const available = data.dates.find((x) => compareDateOnly(parseApiDate(x.date), date));
          return {
            date,
            active: available !== undefined,
            action: ActionEnum.None,
          };
        }
      );

      setTableData((prevData) => {
        if (prevData?.locationId !== data.districtId) {
          return {
            location: data.districtName,
            locationId: data.districtId,
            dates: dates,
            isEditable: isEditable,
          };
        }

        return {
          ...prevData,
          dates: prevData.dates
            .filter(
              (prevDate) => !dates.some((newDate) => compareDateOnly(newDate.date, prevDate.date))
            )
            .concat(dates),
        };
      });

      setIsLoadingButtons(false);
    },
    [isEditable]
  );

  useMemo(() => {
    if (!isDataLoading) {
      generateExamDatesForSelectedDate(selectedDate, data);
    }
  }, [isDataLoading, selectedDate, data, generateExamDatesForSelectedDate]);

  const onDateChange = useCallback(
    (newDate: Date | undefined) => {
      if (newDate) {
        const normalizedDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        if (!compareMonthAndYearOnly(selectedDate, normalizedDate)) {
          setSelectedDate(normalizedDate);
        }
      }
    },
    [selectedDate]
  );

  const onDayClick = useCallback(
    (clickedDate: Date | undefined) => {
      if (!tableData || !clickedDate) return;

      const updatedDates = tableData.dates.map((item) => {
        return compareDateOnly(item.date, clickedDate) ? getNewState(item) : { ...item };
      });

      const newValue = {
        ...tableData,
        dates: updatedDates,
      };

      setTableData(newValue);
    },
    [tableData]
  );

  const customCell = useCallback(
    (day: Date) => {
      if (tableData === undefined) return;
      const date = tableData.dates.find((d) => compareDateOnly(d.date, day))!;
      return (
        <Panel
          date={date}
          onChange={() => {
            return;
          }}
          isEditable={isEditable}
        />
      );
    },
    [isEditable, tableData]
  );

  const onDiscard = () => {
    setTableData((prevData) => {
      return {
        ...prevData!,
        dates: prevData!.dates.map((d) => {
          return {
            date: d.date,
            active: d.action == ActionEnum.None ? d.active : !d.active,
            action: ActionEnum.None,
          };
        }),
      };
    });
  };

  const onSave = () => {
    if (!tableData) return;
    const dateToCreate: DateAvailability[] = [];
    const dateToDelete: DateAvailability[] = [];

    for (const dateItem of tableData.dates) {
      if (dateItem.action === ActionEnum.Added)
        dateToCreate.push({
          districtId: tableData.locationId,
          date: formatApiDate(dateItem.date),
        });
      if (dateItem.action === ActionEnum.Deleted)
        dateToDelete.push({
          districtId: tableData.locationId,
          date: formatApiDate(dateItem.date),
        });
    }

    const SessionDateAvailabilityRequest: SessionDateAvailabilityRequest = {
      dateToCreate: dateToCreate,
      dateToDelete: dateToDelete,
    };

    setTableData((prevData) => {
      return {
        ...prevData!,
        dates: prevData!.dates.map((d) => {
          return {
            ...d,
            action: ActionEnum.None,
          };
        }),
      };
    });
    setIsLoadingButtons(true);
    saveChanges(SessionDateAvailabilityRequest);
  };

  return {
    date: selectedDate,
    locationName,
    isDataLoading,
    isLoadingButtons,
    isModified,
    isEditable,
    onSave,
    onDiscard,
    onDateChange,
    onDayClick,
    customCell,
  };
};
